import { useState } from 'react';
import { X, Calendar, Clock, MapPin, User, AlertTriangle, Loader2, Users, Hourglass } from 'lucide-react';
import type { WaitlistMode } from '../../services/hooks/useClientPortal';

export type ClassVariant = 'enrolled' | 'bookable' | 'full' | 'locked';

// Todos los botones del pie comparten tamaño: `flex-1` los reparte a partes
// iguales y `whitespace-nowrap` evita que una etiqueta larga ("Cancelar clase")
// salte de línea y quede más alta que su pareja en móvil.
// `flex-1` + `min-w-0` reparte el ancho a partes iguales y permite encoger por
// debajo del texto, así una etiqueta larga nunca desborda el modal en móvil.
// La fila usa el `stretch` por defecto: si una etiqueta salta de línea, ambos
// botones conservan la misma altura.
// px-2 (no px-3): con `flex-1` el ancho ya lo marca la fila, y con el padding
// mayor "Cancelar clase" no cabe en una línea a 375 px.
const BTN = 'flex-1 min-w-0 flex items-center justify-center gap-2 rounded border px-2 py-2 text-sm transition-colors disabled:opacity-50';
const BTN_NEUTRAL = `${BTN} border-gray-300 text-gray-700 hover:bg-gray-100`;
const BTN_DANGER_OUTLINE = `${BTN} border-red-300 text-red-600 hover:bg-red-50`;
const BTN_DANGER = `${BTN} border-transparent bg-red-600 text-white hover:bg-red-700`;
const BTN_PRIMARY = `${BTN} border-transparent bg-blue-600 text-white hover:bg-blue-700`;

interface ClientClassDetailModalProps {
  nivelName: string;
  instructorName?: string;
  sala?: string;
  startsAt: Date;
  endLabel?: string;
  variant: ClassVariant;
  availableSlots?: number;
  maxClientes?: number;
  /**
   * Fecha límite (ISO) para gastar la recuperación que daría cancelar esta
   * clase. Se muestra la FECHA, no "X días": el plazo ya no es un número fijo
   * (por norma vence a fin de mes), así que un contador de días mentiría.
   */
  recoveryDeadline?: string;
  // enrolled
  /** Solo es false cuando la clase ya ha empezado; las políticas del centro no bloquean. */
  cancelable?: boolean;
  /** Si esta cancelación da derecho a una recuperación (política del centro). */
  earnsRecovery?: boolean;
  /** Motivo por el que no habrá recuperación, o por el que ya no se puede cancelar. */
  cancelNoticeMessage?: string;
  isCancelling?: boolean;
  onCancelClass?: () => void;
  // bookable
  isBooking?: boolean;
  onBook?: () => void;
  /** Por qué esta clase no se puede reservar (nivel ajeno, sin recuperación, fuera de plazo). */
  lockedReason?: string;
  // full → waitlist (D1)
  waitlistMode?: WaitlistMode;
  /** The client is already queued for this class. */
  onWaitlist?: boolean;
  /** Joining would succeed: mode on, a valid recovery, outside the cutoff. */
  canJoinWaitlist?: boolean;
  /** Why joining isn't possible, when the waitlist is on but this class can't be joined. */
  waitlistBlockedReason?: string;
  isJoiningWaitlist?: boolean;
  onJoinWaitlist?: () => void;
  isLeavingWaitlist?: boolean;
  onLeaveWaitlist?: () => void;
  onClose: () => void;
}

const ClientClassDetailModal = ({
  nivelName,
  instructorName,
  sala,
  startsAt,
  endLabel,
  variant,
  availableSlots,
  maxClientes,
  recoveryDeadline,
  cancelable,
  earnsRecovery = true,
  cancelNoticeMessage,
  isCancelling,
  onCancelClass,
  isBooking,
  onBook,
  lockedReason,
  waitlistMode = 'off',
  onWaitlist = false,
  canJoinWaitlist = false,
  waitlistBlockedReason,
  isJoiningWaitlist,
  onJoinWaitlist,
  isLeavingWaitlist,
  onLeaveWaitlist,
  onClose,
}: ClientClassDetailModalProps) => {
  // Which action ('cancel' for enrolled, 'book' for bookable) is being confirmed.
  const [confirming, setConfirming] = useState(false);

  const dateLabel = startsAt.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  const timeLabel = startsAt.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const deadlineLabel = recoveryDeadline
    ? new Date(recoveryDeadline).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })
    : '';

  const badge = variant === 'full' && onWaitlist
    ? { label: 'En lista de espera', className: 'bg-amber-100 text-amber-800' }
    : {
      enrolled: { label: 'Estás inscrito', className: 'bg-green-100 text-green-700' },
      bookable: { label: 'Disponible para recuperar', className: 'bg-blue-100 text-blue-700' },
      full: { label: 'Clase completa', className: 'bg-red-100 text-red-700' },
      locked: { label: 'No disponible', className: 'bg-gray-100 text-gray-500' },
    }[variant];

  // What joining commits the client to. Must match the confirmation email: the
  // stakeholders asked for it to be explicit that a recovery will be spent.
  const waitlistPromise = waitlistMode === 'auto'
    ? 'Si se libera una plaza, te inscribiremos automáticamente y se registrará una de tus recuperaciones. Te avisaremos por correo.'
    : 'Si se libera una plaza te avisaremos por correo, y será para quien la reserve primero. Al reservarla se registrará una de tus recuperaciones.';

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Clase</p>
            <h2 className="text-lg font-bold text-gray-800 capitalize">{nivelName}</h2>
            <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          {/* -m-2 p-2: área de toque de 36px en móvil sin desplazar el layout */}
          <button onClick={onClose} className="-m-2 p-2 text-gray-400 hover:text-gray-600" aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        {/* Details */}
        <div className="p-5 space-y-3 text-sm text-gray-700">
          <div className="flex items-center gap-3">
            <Calendar size={18} className="text-gray-400 shrink-0" />
            <span className="capitalize">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-3">
            <Clock size={18} className="text-gray-400 shrink-0" />
            <span>{timeLabel}{endLabel ? ` – ${endLabel}` : ''}</span>
          </div>
          {sala && (
            <div className="flex items-center gap-3">
              <MapPin size={18} className="text-gray-400 shrink-0" />
              <span>Sala {sala}</span>
            </div>
          )}
          {instructorName && (
            <div className="flex items-center gap-3">
              <User size={18} className="text-gray-400 shrink-0" />
              <span>{instructorName}</span>
            </div>
          )}
          {typeof availableSlots === 'number' && typeof maxClientes === 'number' && variant !== 'enrolled' && (
            <div className="flex items-center gap-3">
              <Users size={18} className="text-gray-400 shrink-0" />
              {/* Ocupación: ocupadas/total (p. ej. 2/4) */}
              <span>{Math.max(0, maxClientes - availableSlots)}/{maxClientes} plazas ocupadas</span>
            </div>
          )}
        </div>

        {/* Footer / actions */}
        <div className="p-5 border-t border-gray-100 bg-gray-50">
          {/* ENROLLED → cancel */}
          {variant === 'enrolled' && (
            confirming ? (
              <div className="space-y-3">
                {/* Sin recuperación el aviso pesa más: es lo que el cliente
                    pierde al confirmar, y tiene que verlo antes de hacerlo. */}
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <AlertTriangle
                    size={18}
                    className={`shrink-0 mt-0.5 ${earnsRecovery ? 'text-yellow-500' : 'text-red-500'}`}
                  />
                  {earnsRecovery ? (
                    <p>
                      ¿Cancelar esta clase?{' '}
                      {deadlineLabel
                        ? <>Podrás recuperarla hasta el <strong>{deadlineLabel}</strong>.</>
                        : <>Ganarás una <strong>recuperación</strong>.</>}
                    </p>
                  ) : (
                    <p>
                      ¿Cancelar esta clase? <strong>No tendrás recuperación</strong>: se perderá.
                      {cancelNoticeMessage ? ` ${cancelNoticeMessage}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirming(false)} disabled={isCancelling}
                    className={BTN_NEUTRAL}>
                    Mantener
                  </button>
                  <button onClick={onCancelClass} disabled={isCancelling}
                    className={BTN_DANGER}>
                    {isCancelling && <Loader2 size={16} className="shrink-0 animate-spin" />}
                    {isCancelling ? 'Cancelando…' : 'Sí, cancelar'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Cancelar fuera de plazo se permite, pero se avisa ANTES de
                    pulsar: el cliente decide sabiendo que pierde la clase. */}
                {cancelable && !earnsRecovery && cancelNoticeMessage && (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                    <span>{cancelNoticeMessage}</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={onClose} className={BTN_NEUTRAL}>
                    Cerrar
                  </button>
                  {cancelable ? (
                    <button onClick={() => setConfirming(true)} className={BTN_DANGER_OUTLINE}>
                      Cancelar clase
                    </button>
                  ) : (
                    <span className="flex-1 text-xs text-gray-400 text-right self-center">{cancelNoticeMessage}</span>
                  )}
                </div>
              </div>
            )
          )}

          {/* BOOKABLE → book a make-up */}
          {variant === 'bookable' && (
            confirming ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <AlertTriangle size={18} className="text-blue-500 shrink-0 mt-0.5" />
                  <p>¿Reservar esta recuperación? Usará una de tus recuperaciones pendientes.</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirming(false)} disabled={isBooking}
                    className={BTN_NEUTRAL}>
                    Atrás
                  </button>
                  <button onClick={onBook} disabled={isBooking} className={BTN_PRIMARY}>
                    {isBooking && <Loader2 size={16} className="shrink-0 animate-spin" />}
                    {isBooking ? 'Reservando…' : 'Sí, reservar'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button onClick={onClose} className={BTN_NEUTRAL}>
                  Cerrar
                </button>
                <button onClick={() => setConfirming(true)} className={BTN_PRIMARY}>
                  Reservar
                </button>
              </div>
            )
          )}

          {/* FULL + already queued → leave the waitlist (D1) */}
          {variant === 'full' && onWaitlist && (
            confirming ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <AlertTriangle size={18} className="text-yellow-500 shrink-0 mt-0.5" />
                  <p>
                    ¿Salir de la lista de espera? Si vuelves a apuntarte,{' '}
                    <strong>entrarás al final de la lista</strong>.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirming(false)} disabled={isLeavingWaitlist} className={BTN_NEUTRAL}>
                    Seguir en la lista
                  </button>
                  <button onClick={onLeaveWaitlist} disabled={isLeavingWaitlist} className={BTN_DANGER}>
                    {isLeavingWaitlist && <Loader2 size={16} className="shrink-0 animate-spin" />}
                    {isLeavingWaitlist ? 'Saliendo…' : 'Sí, salir'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  <Hourglass size={14} className="shrink-0 mt-0.5" />
                  <span>Estás en la lista de espera. {waitlistPromise}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={onClose} className={BTN_NEUTRAL}>
                    Cerrar
                  </button>
                  <button onClick={() => setConfirming(true)} className={BTN_DANGER_OUTLINE}>
                    Salir de la lista
                  </button>
                </div>
              </div>
            )
          )}

          {/* FULL + can queue → join the waitlist (D1) */}
          {variant === 'full' && !onWaitlist && canJoinWaitlist && (
            confirming ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-sm text-gray-700">
                  <Hourglass size={18} className="text-amber-500 shrink-0 mt-0.5" />
                  <p>¿Apuntarte a la lista de espera? {waitlistPromise}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirming(false)} disabled={isJoiningWaitlist} className={BTN_NEUTRAL}>
                    Atrás
                  </button>
                  <button onClick={onJoinWaitlist} disabled={isJoiningWaitlist} className={BTN_PRIMARY}>
                    {isJoiningWaitlist && <Loader2 size={16} className="shrink-0 animate-spin" />}
                    {isJoiningWaitlist ? 'Apuntando…' : 'Sí, apuntarme'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Esta clase está completa, pero puedes apuntarte a su lista de espera.
                </p>
                <div className="flex gap-2">
                  <button onClick={onClose} className={BTN_NEUTRAL}>
                    Cerrar
                  </button>
                  <button onClick={() => setConfirming(true)} className={BTN_PRIMARY}>
                    Lista de espera
                  </button>
                </div>
              </div>
            )
          )}

          {/* FULL (not joinable) / LOCKED → info only */}
          {((variant === 'full' && !onWaitlist && !canJoinWaitlist) || variant === 'locked') && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                {variant === 'full'
                  ? `Esta clase está completa.${waitlistMode !== 'off' && waitlistBlockedReason ? ` ${waitlistBlockedReason}` : ''}`
                  : lockedReason || 'Para reservar aquí necesitas una recuperación pendiente.'}
              </p>
              <button onClick={onClose} className={BTN_NEUTRAL}>
                Cerrar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientClassDetailModal;
